/**
 * Pure policy for fitting an animated module theme to the device: renderer
 * classification, the QA override, the render mode, pixel-ratio math, the
 * rolling frame-cost window and the session memory format. No DOM access, so
 * every rule here is unit tested; `renderEnvironment.ts` and
 * `useFlowThemeRenderPolicy.ts` wire it to the browser.
 *
 * Background: with GPU canvas acceleration
 * every theme holds 60fps. With software canvas rendering (VMs, remote
 * desktops, blocklisted drivers, acceleration switched off) raster fill-rate
 * dominates, most themes fall to 7-18fps and the main thread is 95-98% busy,
 * so typing and clicks lag too. Fill cost scales with backing-store pixels,
 * which is why dropping the pixel ratio is the main lever, and why a still
 * frame is the fallback once even the lowest ratio can't keep up.
 */

export type RendererClass = 'hardware' | 'software' | 'unknown';

/**
 * What the learner gets. Reported as `mode` in `flow_theme_render_mode`, so
 * the values are an analytics contract: don't rename them.
 */
export type FlowThemeRenderMode = 'animated' | 'still_software' | 'still_slow' | 'still_reduced_motion';

/** QA and demo override (`?flowThemeMode=` or the `wf.flowThemeMode` storage flag). */
export type FlowThemeModeOverride = 'animated' | 'still' | 'software';

/** `fallback_reason` analytics values. */
export type FlowThemeFallbackReason = 'software_rendering' | 'sustained_slow';

/**
 * What this tab learned on earlier screens. `heavy` means an ordinary theme
 * couldn't keep up; `all` means even a `softwareFriendly` theme couldn't.
 */
export type SessionSlowness = 'heavy' | 'all';

export interface SessionRenderMemory {
  /** Learned backing-store budget in device pixels (width x height x ratio^2). */
  pixelBudget?: number;
  slow?: SessionSlowness;
}

// ---------------------------------------------------------------------------
// Resolution

/** High-DPI cap: past 2x the soft scenes gain nothing visible. */
export const MAX_PIXEL_RATIO = 2;
/**
 * Lowest ratio the adaptive loop goes to. The scenes are soft light and
 * gradients, so the CSS upscale of a half-resolution canvas reads fine; in the
 * study this lifted 8 themes from under 20fps to 33-49fps in software.
 */
export const MIN_PIXEL_RATIO = 0.5;
/** Backing-store budget (~2560x1440), as before: big screens trade a little sharpness for frame rate. */
export const MAX_CANVAS_PIXELS = 3_700_000;
/**
 * Still frames held for performance (software rendering or a slow device) are
 * drawn at up to 1x, ignoring the animated budget: there is one per step, so
 * they can afford more than a 0.5x animated frame, but each is a full
 * software raster (100-300ms at 2x on a slow CPU) drawn while the card moves.
 */
export const PERFORMANCE_STILL_MAX_PIXEL_RATIO = 1;

/** Pixel-ratio cap for a render mode (`undefined` = the normal cap). */
export const maxPixelRatioForMode = (mode: FlowThemeRenderMode): number | undefined =>
  mode === 'still_software' || mode === 'still_slow' ? PERFORMANCE_STILL_MAX_PIXEL_RATIO : undefined;

export interface PixelRatioBounds {
  /** Highest ratio allowed: device ratio, `MAX_PIXEL_RATIO` and `MAX_CANVAS_PIXELS`. */
  cap: number;
  /** Lowest ratio the adaptive loop may use (never above `cap`). */
  floor: number;
  /** Where to start: the cap, or less when a learned pixel budget says so. */
  ratio: number;
}

/**
 * Pixel-ratio bounds for a canvas of `width` x `height` CSS pixels.
 * `pixelBudget` (from session memory) starts the canvas at the ratio whose
 * backing store fits that many pixels; `maxRatio` caps it further.
 */
export const fitPixelRatio = ({
  width,
  height,
  devicePixelRatio,
  pixelBudget,
  maxRatio = MAX_PIXEL_RATIO,
}: {
  width: number;
  height: number;
  devicePixelRatio: number;
  pixelBudget?: number | null;
  maxRatio?: number;
}): PixelRatioBounds => {
  const area = Math.max(1, width) * Math.max(1, height);
  const dpr = Number.isFinite(devicePixelRatio) && devicePixelRatio > 0 ? devicePixelRatio : 1;
  const cap = Math.min(dpr, MAX_PIXEL_RATIO, maxRatio, Math.sqrt(MAX_CANVAS_PIXELS / area));
  const floor = Math.min(MIN_PIXEL_RATIO, cap);
  const budgetRatio = pixelBudget && pixelBudget > 0 ? Math.sqrt(pixelBudget / area) : cap;
  return { cap, floor, ratio: clampRatio(budgetRatio, floor, cap) };
};

const clampRatio = (value: number, floor: number, cap: number) => Math.min(cap, Math.max(floor, value));

/** Device pixels in a backing store (what software fill cost scales with). */
export const backingPixels = (width: number, height: number, ratio: number) =>
  Math.max(1, width) * Math.max(1, height) * ratio * ratio;

/**
 * Software fill cost scales roughly with pixel count, i.e. with ratio^2, so
 * the ratio that fits a frame budget is `ratio * sqrt(target / measured)`.
 * Fixed per-frame costs (JS, compositing) don't shrink, so this undershoots a
 * little; the next window corrects it.
 */
export const ratioForFrameBudget = (
  ratio: number,
  measuredMs: number,
  targetMs: number,
  floor: number,
  cap: number,
) => (measuredMs > 0 ? clampRatio(ratio * Math.sqrt(targetMs / measuredMs), floor, cap) : clampRatio(ratio, floor, cap));

// ---------------------------------------------------------------------------
// Renderer classification

/**
 * Renderer strings that only software rasterisers report:
 * - SwiftShader: Chrome's CPU fallback, including `ANGLE (Google, Vulkan ... (SwiftShader Device ...))`.
 * - llvmpipe / softpipe: Mesa's CPU drivers on Linux and in many VMs.
 * - "Microsoft Basic Render Driver": Windows' WARP fallback when no GPU driver is installed.
 * - "Apple Software Renderer", Mesa's "software rasterizer", "Mesa OffScreen".
 * Real GPU names never contain these words; `\bsoftware\b` is a whole word so
 * vendor names that merely contain the letters can't match. Virtual GPUs
 * (VMware SVGA, Parallels, VirtualBox) are deliberately not listed: some are
 * accelerated, so they are left to the runtime fallback instead.
 */
const SOFTWARE_RENDERER_PATTERN = /swiftshader|llvmpipe|softpipe|software\s*rasterizer|basic render driver|mesa offscreen|\bsoftware\b/i;

export interface RendererProbeResult {
  /** WebGL exists in this browser at all. */
  webglSupported: boolean;
  /** `getContext('webgl', { failIfMajorPerformanceCaveat: true })` returned a context. */
  performantContext: boolean;
  /** `getContext('webgl')` without the caveat flag returned a context. */
  anyContext: boolean;
  /** Unmasked renderer when the browser shares it, else `gl.RENDERER`; never sent anywhere. */
  renderer: string | null;
}

/**
 * Heuristics, most specific first:
 * 1. A software renderer string is conclusive.
 * 2. A context with `failIfMajorPerformanceCaveat` means the browser itself
 *    sees no major performance caveat: hardware. (Masked renderers such as
 *    Safari's "Apple GPU" land here too.)
 * 3. WebGL works only WITHOUT that flag: the browser is on a blocklisted or
 *    software path, which also leaves 2D canvas unaccelerated: software.
 * 4. No WebGL at all (disabled by policy, privacy settings, old browsers) says
 *    nothing about 2D canvas: unknown, left to the runtime fallback. Treating
 *    it as software would put every privacy-hardened browser on still frames.
 */
export const classifyRenderer = (probe: RendererProbeResult): RendererClass => {
  if (probe.renderer && SOFTWARE_RENDERER_PATTERN.test(probe.renderer)) {
    return 'software';
  }
  if (!probe.webglSupported) {
    return 'unknown';
  }
  if (probe.performantContext) {
    return 'hardware';
  }
  return probe.anyContext ? 'software' : 'unknown';
};

// ---------------------------------------------------------------------------
// Render mode

const OVERRIDES: readonly FlowThemeModeOverride[] = ['animated', 'still', 'software'];

/** Parses an override value; `auto` clears a stored override. Anything else is ignored. */
export const parseModeOverride = (value: string | null | undefined): FlowThemeModeOverride | 'auto' | null => {
  const normalized = (value ?? '').trim().toLowerCase();
  if (normalized === 'auto') {
    return 'auto';
  }
  return (OVERRIDES as readonly string[]).includes(normalized) ? (normalized as FlowThemeModeOverride) : null;
};

export interface RenderPolicyInput {
  reducedMotion: boolean;
  rendererClass: RendererClass;
  sessionSlowness: SessionSlowness | null;
  /** The theme's definition says it holds its frame rate in software rendering. */
  softwareFriendly: boolean;
  override: FlowThemeModeOverride | null;
}

export interface RenderPolicy {
  mode: FlowThemeRenderMode;
  /** Ask the scene for its lighter particle counts. */
  lowQuality: boolean;
  /**
   * May the canvas switch to still frames on its own when frames stay slow?
   * Off only for the `animated` override, so a demo on a slow machine keeps moving.
   */
  allowStillFallback: boolean;
  /** Frames are being held still because of the renderer (for the fallback event). */
  heldBySoftware: boolean;
}

/**
 * Decides how a theme renders before its first frame. Reduced motion always
 * wins (accessibility, even over the QA override). A `softwareFriendly` theme
 * keeps animating at low quality where others are held still, unless this tab
 * already saw even a friendly theme fail.
 */
export const resolveRenderPolicy = ({
  reducedMotion,
  rendererClass,
  sessionSlowness,
  softwareFriendly,
  override,
}: RenderPolicyInput): RenderPolicy => {
  const still = (mode: FlowThemeRenderMode, heldBySoftware = false): RenderPolicy => ({
    mode,
    lowQuality: false,
    allowStillFallback: false,
    heldBySoftware,
  });
  if (reducedMotion) {
    return still('still_reduced_motion');
  }
  if (override === 'animated') {
    return { mode: 'animated', lowQuality: false, allowStillFallback: false, heldBySoftware: false };
  }
  if (override === 'still') {
    return still('still_slow');
  }
  const software = override === 'software' || rendererClass === 'software';
  if (sessionSlowness === 'all' || (sessionSlowness === 'heavy' && !softwareFriendly)) {
    return still('still_slow');
  }
  if (software) {
    return softwareFriendly
      ? { mode: 'animated', lowQuality: true, allowStillFallback: true, heldBySoftware: false }
      : still('still_software', true);
  }
  return {
    mode: 'animated',
    // A tab that already fell back once keeps friendly themes light.
    lowQuality: sessionSlowness !== null,
    allowStillFallback: true,
    heldBySoftware: false,
  };
};

// ---------------------------------------------------------------------------
// Frame budget

/** Frame costs older than this leave the rolling window. */
export const BUDGET_WINDOW_MS = 1500;
/** A decision needs this much observed time since the last change (mount, resize, ratio change). */
export const BUDGET_DECIDE_AFTER_MS = 1000;
/** ...and at least this many frames (a 2fps device still decides within about 1.5s). */
export const BUDGET_MIN_SAMPLES = 3;
/**
 * Median frame cost above this is slow. Kept above 30Hz frame times (33ms),
 * so displays capped at 30fps (battery saver, iOS Low Power Mode) never lose
 * resolution, and just above a 24-25Hz refresh (40-42ms).
 */
export const SLOW_FRAME_MS = 42;
/** The frame cost a downgrade aims for (~40fps), leaving room for fixed costs. */
export const TARGET_FRAME_MS = 25;
/**
 * At the ratio floor, a median above this (under ~22fps, with the main thread
 * saturated so input lags) means animation can't be rescued: go still.
 */
export const STILL_FRAME_MS = 45;
/** p95 under this is "lots of headroom" (60Hz frames land at ~16.7ms). */
export const FAST_FRAME_MS = 20;
/** Continuous headroom needed before a cautious upgrade. */
export const UPGRADE_AFTER_MS = 10_000;
/** One upgrade step: x1.25 ratio is about 1.56x the pixels. */
export const UPGRADE_FACTOR = 1.25;
/** Slow again this soon after an upgrade: the upgrade failed, so wait twice as long next time. */
export const FAILED_UPGRADE_WINDOW_MS = 5000;
export const MAX_UPGRADE_HOLD_MS = 80_000;
const RATIO_EPSILON = 0.01;

export interface FrameCostStats {
  count: number;
  p50: number;
  p95: number;
}

/** Nearest-rank percentile of an ascending array. */
export const percentile = (sorted: readonly number[], q: number) => {
  if (!sorted.length) {
    return 0;
  }
  const rank = Math.ceil(q * sorted.length) - 1;
  return sorted[Math.min(sorted.length - 1, Math.max(0, rank))];
};

export const summarizeFrameCosts = (costs: readonly number[]): FrameCostStats => {
  const sorted = [...costs].sort((a, b) => a - b);
  return { count: sorted.length, p50: percentile(sorted, 0.5), p95: percentile(sorted, 0.95) };
};

export type BudgetDecision =
  | { type: 'none' }
  | { type: 'resize'; ratio: number; direction: 'down' | 'up'; stats: FrameCostStats }
  | { type: 'still'; stats: FrameCostStats };

const NONE: BudgetDecision = { type: 'none' };

/**
 * Rolling-window frame budget for one canvas. Feed it one cost per drawn
 * frame (in ms, at that frame's timestamp) and act on the decision.
 *
 * - Slow (median over `SLOW_FRAME_MS` for a full window, about 1s): jump
 *   straight to the ratio that should fit `TARGET_FRAME_MS`, then judge the
 *   new ratio on a fresh window.
 * - Still slow at the floor (median over `STILL_FRAME_MS` for a fresh window):
 *   switch to still frames, when allowed.
 * - Lots of headroom (p95 under `FAST_FRAME_MS` for `UPGRADE_AFTER_MS`): one
 *   cautious x1.25 step up, never above the cap. An upgrade that turns slow
 *   within 5s is undone by the normal downgrade and doubles the wait before
 *   the next try, so the ratio can't oscillate.
 */
export class FrameBudget {
  private samples: Array<{ at: number; cost: number }> = [];

  private observedSince: number;

  private fastSince: number | null = null;

  private lastUpgradeAt: number | null = null;

  private upgradeHoldMs = UPGRADE_AFTER_MS;

  /** Stats of the latest full window, for reporting. */
  latest: FrameCostStats | null = null;

  ratio: number;

  floor: number;

  cap: number;

  constructor(bounds: PixelRatioBounds, now: number, private readonly allowStill = true) {
    this.ratio = bounds.ratio;
    this.floor = bounds.floor;
    this.cap = bounds.cap;
    this.observedSince = now;
  }

  /** New bounds (a viewport resize): keep learning, but judge the new size afresh. */
  setBounds(bounds: PixelRatioBounds, now: number) {
    this.ratio = bounds.ratio;
    this.floor = bounds.floor;
    this.cap = bounds.cap;
    this.restart(now);
  }

  /** Forget the window (after a pause, so the gap before it isn't counted). */
  restart(now: number) {
    this.samples = [];
    this.observedSince = now;
    this.fastSince = null;
  }

  get atFloor() {
    return this.ratio <= this.floor + RATIO_EPSILON;
  }

  record(at: number, cost: number): BudgetDecision {
    if (!Number.isFinite(cost) || cost < 0) {
      return NONE;
    }
    this.samples.push({ at, cost });
    const cutoff = at - BUDGET_WINDOW_MS;
    while (this.samples.length > BUDGET_MIN_SAMPLES && this.samples[0].at < cutoff) {
      this.samples.shift();
    }
    if (at - this.observedSince < BUDGET_DECIDE_AFTER_MS || this.samples.length < BUDGET_MIN_SAMPLES) {
      return NONE;
    }
    const stats = summarizeFrameCosts(this.samples.map((sample) => sample.cost));
    this.latest = stats;

    if (stats.p50 > SLOW_FRAME_MS) {
      this.fastSince = null;
      if (!this.atFloor) {
        if (this.lastUpgradeAt !== null && at - this.lastUpgradeAt < FAILED_UPGRADE_WINDOW_MS) {
          this.upgradeHoldMs = Math.min(MAX_UPGRADE_HOLD_MS, this.upgradeHoldMs * 2);
        }
        this.ratio = ratioForFrameBudget(this.ratio, stats.p50, TARGET_FRAME_MS, this.floor, this.cap);
        this.restart(at);
        return { type: 'resize', ratio: this.ratio, direction: 'down', stats };
      }
      return this.allowStill && stats.p50 > STILL_FRAME_MS ? { type: 'still', stats } : NONE;
    }

    if (stats.p95 < FAST_FRAME_MS && this.ratio < this.cap - RATIO_EPSILON) {
      this.fastSince = this.fastSince ?? this.samples[0].at;
      if (at - this.fastSince >= this.upgradeHoldMs) {
        this.ratio = Math.min(this.cap, this.ratio * UPGRADE_FACTOR);
        this.lastUpgradeAt = at;
        this.restart(at);
        return { type: 'resize', ratio: this.ratio, direction: 'up', stats };
      }
    } else {
      this.fastSince = null;
    }
    return NONE;
  }
}

// ---------------------------------------------------------------------------
// Session memory

/** Parses stored session memory, dropping anything malformed. */
export const parseRenderMemory = (raw: string | null | undefined): SessionRenderMemory => {
  if (!raw) {
    return {};
  }
  try {
    const value: unknown = JSON.parse(raw);
    if (!value || typeof value !== 'object') {
      return {};
    }
    const { pixelBudget, slow } = value as Record<string, unknown>;
    const memory: SessionRenderMemory = {};
    if (typeof pixelBudget === 'number' && Number.isFinite(pixelBudget) && pixelBudget > 0) {
      memory.pixelBudget = pixelBudget;
    }
    if (slow === 'heavy' || slow === 'all') {
      memory.slow = slow;
    }
    return memory;
  } catch {
    return {};
  }
};

/** `all` outranks `heavy`; a later, milder report never clears it. */
export const mergeSlowness = (current: SessionSlowness | undefined, next: SessionSlowness): SessionSlowness =>
  current === 'all' || next === 'all' ? 'all' : 'heavy';
